import { Checkbox, Flex, FormControl, FormErrorMessage, FormLabel, Select, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { COUNTRY_OPTIONS } from "@equidefi/constants/countries";
import { SmartFormProvider, SmartSelect } from "@equidefi/portals/components/smart-form";
import { postalCodeZodRefinement } from "@equidefi/portals/helpers/postalCode";
import { usePostalCodeExcluded } from "@equidefi/portals/hooks/usePostalCode";
import type { DTO } from "@equidefi/shared/types/dto";
import { Heading } from "@equidefi/ui";

// @ts-ignore
import { MESSAGE } from "@equidefi/constants/forms";
import { TRUST_SUB_TYPE_KEYS, TRUST_SUB_TYPE_OPTIONS } from "@equidefi/constants/profile";
import { SavedProfileFormContainer } from "./SavedProfileFormContainer";

const schema = z
  .object({
    ta_sub_type: z.enum(TRUST_SUB_TYPE_KEYS, {
      message: MESSAGE.REQUIRED("Trust Sub-type"),
    }),
    company: z.string().trim().min(1, MESSAGE.REQUIRED("Trust Name")),
    ein: z.string().trim().min(1, MESSAGE.REQUIRED("EIN")),
    entity_address_street_1: z.string().trim().min(1, MESSAGE.REQUIRED("Street Address")),
    entity_address_street_2: z.string().nullable(),
    entity_address_city: z.string().trim().min(1, MESSAGE.REQUIRED("City")),
    entity_address_state: z.string().trim().min(1, MESSAGE.REQUIRED("State")),
    entity_address_postal_code: z.string().trim().optional().nullable(),
    entity_address_country_code: z.string().trim().min(1, MESSAGE.REQUIRED("Country")),
  })
  .superRefine(
    postalCodeZodRefinement({
      countryField: "entity_address_country_code",
      postalField: "entity_address_postal_code",
      message: MESSAGE.REQUIRED("Postal Code"),
    }),
  );

export type TrustAccountTypeFormValues = z.infer<typeof schema>;

type TrustAccountTypeFormNormalized = {
  ta_sub_type: string;
  company: string;
  ein: string;
  entity_address_street_1: string;
  entity_address_street_2: string | null;
  entity_address_city: string;
  entity_address_state: string;
  entity_address_postal_code: string;
  entity_address_country_code: string;
};

type TrustAccountTypeFormProps = {
  profile: DTO.SavedProfile;
  onSave: (values: TrustAccountTypeFormNormalized) => Promise<void>;
};

const getInitialValues = (profile: DTO.SavedProfile) => ({
  ta_sub_type: profile.ta_sub_type ?? "",
  company: profile.company ?? "",
  ein: profile.ein ?? "",
  entity_address_street_1: profile.entity_address_street_1 ?? "",
  entity_address_street_2: profile.entity_address_street_2 ?? null,
  entity_address_city: profile.entity_address_city ?? "",
  entity_address_state: profile.entity_address_state ?? "",
  entity_address_postal_code: profile.entity_address_postal_code ?? "",
  entity_address_country_code: profile.entity_address_country_code ?? "",
});

const normalizeValues = (values: TrustAccountTypeFormValues): TrustAccountTypeFormNormalized => ({
  ta_sub_type: values.ta_sub_type,
  company: values.company.trim(),
  ein: values.ein.trim(),
  entity_address_street_1: values.entity_address_street_1.trim(),
  entity_address_street_2: values.entity_address_street_2?.trim() || null,
  entity_address_city: values.entity_address_city.trim(),
  entity_address_state: values.entity_address_state.trim(),
  entity_address_postal_code: values.entity_address_postal_code?.trim() || "",
  entity_address_country_code: values.entity_address_country_code.trim(),
});

export function TrustAccountTypeForm({ profile, onSave }: TrustAccountTypeFormProps) {
  const toast = useToast();
  const [isEinVisible, setIsEinVisible] = useState(false);

  const form = useForm<TrustAccountTypeFormValues>({
    defaultValues: getInitialValues(profile),
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const hidePostalCode = usePostalCodeExcluded(
    form.watch("entity_address_country_code"),
    form.watch("entity_address_postal_code"),
    () => form.setValue("entity_address_postal_code", "", { shouldValidate: true }),
  );

  const onSubmit = useCallback(
    async (values: TrustAccountTypeFormValues) => {
      try {
        await onSave(normalizeValues(values));
      } catch (error) {
        console.error(error);

        toast({
          status: "error",
          description: "There was an error trying to save your trust account type.",
        });
      }
    },
    [onSave, toast],
  );

  return (
    <SmartFormProvider form={form}>
      {({ Input }) => (
        <SavedProfileFormContainer
          profile={profile}
          title="Trust Details"
          onSave={form.handleSubmit(onSubmit)}
          isLoading={form.formState.isSubmitting}
          isDisabled={!form.formState.isValid}>
          <Controller
            name="ta_sub_type"
            control={form.control}
            render={({ field, fieldState }) => (
              <FormControl isRequired isInvalid={!!fieldState.error}>
                <FormLabel>Trust Sub-type</FormLabel>

                <Select
                  placeholder="Select Sub-type"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  bg="white">
                  {TRUST_SUB_TYPE_OPTIONS.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>

                {fieldState.error && <FormErrorMessage>{fieldState.error.message}</FormErrorMessage>}
              </FormControl>
            )}
          />

          <Input name="company" label="Trust Name" isRequired />

          <Flex gap={3} align="flex-end">
            <Input name="ein" label="EIN" isRequired type={isEinVisible ? "text" : "password"} />

            <Checkbox
              mb={2}
              isChecked={isEinVisible}
              onChange={(e) => setIsEinVisible(e.target.checked)}
              size="sm"
              colorScheme="purple"
              whiteSpace="nowrap">
              Show EIN
            </Checkbox>
          </Flex>

          <Heading textStyle="h2" m={0} mt={4} mb={2} fontWeight="700">
            Trust Address
          </Heading>

          <Input name="entity_address_street_1" label="Street Address" isRequired />
          <Input name="entity_address_street_2" label="Street Address 2 (Unit #)" />
          <Input name="entity_address_city" label="City" isRequired />
          <Input name="entity_address_state" label="State" isRequired />
          {!hidePostalCode && <Input name="entity_address_postal_code" label="Postal Code" isRequired />}
          <SmartSelect name="entity_address_country_code" label="Country" options={COUNTRY_OPTIONS} isRequired />
        </SavedProfileFormContainer>
      )}
    </SmartFormProvider>
  );
}
